import React from "react";
import {
  Heading,
  VStack,
  Stack,
  Text,
  Box,
  UnorderedList,
  ListItem,
  Tooltip,
} from "@chakra-ui/react";
import { CherryPayApi } from "../../api/models";
import { ViewControl } from "../ViewControl/ViewControl";
import DateUtil from "../../util/DateUtil";
import { InfoIcon } from "../../styles/icons";

interface ExtendedFieldsDisplayProps {
  member: CherryPayApi.Member;
  fieldDefinitions: CherryPayApi.ExtendedFieldDefinition[];
}

export const ExtendedFieldsDisplay: React.FC<ExtendedFieldsDisplayProps> = ({
  member,
  fieldDefinitions,
}) => {
  // Sort field definitions by FieldOrder
  const sortedFields = [...fieldDefinitions].sort(
    (a, b) => (a.FieldOrder ?? 0) - (b.FieldOrder ?? 0)
  );

  // Check if the formatted value should use ViewControl's children prop instead of value prop
  // This is necessary for block-level elements (Box, UnorderedList) to avoid DOM nesting warnings
  const isBlockElement = (value: React.ReactNode): boolean => {
    return (
      React.isValidElement(value) &&
      (value.type === Box || value.type === UnorderedList)
    );
  };

  // Format value based on field type
  const formatFieldValue = (
    fieldDef: CherryPayApi.ExtendedFieldDefinition,
    value: unknown
  ): React.ReactNode => {
    if (value == null || value === undefined || value === "") {
      // For Toggle fields, treat empty/null as "No"
      if (fieldDef.FieldType === "Toggle") {
        return "No";
      }
      return "-";
    }

    const { FieldType } = fieldDef;

    switch (FieldType) {
      case "Date":
        // Format date as "Month day, year" (e.g., "December 25, 2025")
        if (typeof value === "string") {
          const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (match) {
            const date = new Date(
              parseInt(match[1]),
              parseInt(match[2]) - 1,
              parseInt(match[3])
            );
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
          }
        }
        return String(value);

      case "DateTime":
        // Format datetime as "dd MMM yyyy, hh:mm:ss AM/PM"
        if (typeof value === "string") {
          const cleaned = value.replace(/\.\d+Z?$/, "");
          const match = cleaned.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
          );
          if (match) {
            const date = new Date(
              parseInt(match[1]),
              parseInt(match[2]) - 1,
              parseInt(match[3]),
              parseInt(match[4]),
              parseInt(match[5]),
              parseInt(match[6])
            );
            return date.toLocaleString("en-AU", {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "numeric",
              minute: "numeric",
              second: "numeric",
              hour12: true,
            });
          }
        }
        return String(value);

      case "Time":
        // Display time in 12-hour format
        return DateUtil.formatTimeToAmPm(String(value));

      case "Toggle":
        // Display boolean as Yes/No text
        if (typeof value === "boolean") {
          return value ? "Yes" : "No";
        }
        return "No";

      case "Number":
      case "NumberSelection":
        // Format number
        if (typeof value === "number") {
          const decimalPlaces = fieldDef.NumberAttributes?.DecimalPlaces ?? 0;
          return value.toFixed(decimalPlaces);
        }
        return String(value);

      case "NumberMultiSelection":
        // Display multiple numbers as a list
        if (Array.isArray(value) && value.length > 0) {
          const decimalPlaces = fieldDef.NumberAttributes?.DecimalPlaces ?? 0;
          return (
            <UnorderedList
              spacing={1}
              fontSize="sm"
              fontWeight="semibold"
              color="cherryGrey.1100"
            >
              {value.map((val, idx) => (
                <ListItem key={idx}>
                  {typeof val === "number"
                    ? val.toFixed(decimalPlaces)
                    : String(val)}
                </ListItem>
              ))}
            </UnorderedList>
          );
        }
        return "-";

      case "TextArea":
        // Display text area content, preserve line breaks
        const textAreaValue = String(value);
        return (
          <Box
            whiteSpace="pre-wrap"
            fontSize="sm"
            fontWeight="semibold"
            color="cherryGrey.1100"
          >
            {textAreaValue}
          </Box>
        );

      case "Text":
      case "TextSelection":
        // Display text, preserve line breaks if multiline
        const textValue = String(value);
        if (textValue.includes("\n")) {
          return (
            <Box whiteSpace="pre-wrap" fontSize="sm">
              {textValue}
            </Box>
          );
        }
        return textValue;

      case "TextMultiSelection":
        // Display multiple text values as a list
        if (Array.isArray(value) && value.length > 0) {
          return (
            <UnorderedList
              spacing={1}
              fontSize="sm"
              fontWeight="semibold"
              color="cherryGrey.1100"
            >
              {value.map((val, idx) => (
                <ListItem key={idx}>{String(val)}</ListItem>
              ))}
            </UnorderedList>
          );
        }
        return "-";

      default:
        return String(value);
    }
  };

  // If no field definitions, don't show anything
  if (sortedFields.length === 0) {
    return null;
  }

  return (
    <Stack
      direction={{ base: "column", xl: "row" }}
      align="start"
      w="100%"
      spacing={6}
    >
      <VStack align="start" w="100%">
        <Heading variant="view-heading">Extended Fields</Heading>
        <VStack align="start" w="100%" spacing={6} pb={10}>
          {sortedFields.map((fieldDef) => {
            const fieldValue = member.ExtendedFields?.[fieldDef.id];
            const formattedValue = formatFieldValue(fieldDef, fieldValue);

            const labelProps = fieldDef.Description
              ? {
                  leftElement: (
                    <Text alignSelf="flex-start" variant="view-label" flex={1}>
                      {fieldDef.FieldName}:{" "}
                      <Tooltip label={fieldDef.Description} placement="right">
                        <span>
                          <InfoIcon />
                        </span>
                      </Tooltip>
                    </Text>
                  ),
                }
              : { label: `${fieldDef.FieldName}:` };

            // Use children prop for block-level elements to avoid DOM nesting warnings
            // (Box and UnorderedList cannot be children of <p> tags)
            // Wrap in Box with flex:3 to match the layout of regular values
            if (isBlockElement(formattedValue)) {
              return (
                <ViewControl key={fieldDef.id} {...labelProps}>
                  <Box flex={3}>{formattedValue}</Box>
                </ViewControl>
              );
            }

            return (
              <ViewControl
                key={fieldDef.id}
                {...labelProps}
                value={formattedValue}
              />
            );
          })}
        </VStack>
      </VStack>
    </Stack>
  );
};
